import { useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useUsers } from '../../hooks/useUsers';
import { useOpenDm } from '../../hooks/useTeamChat';
import { useAuthStore } from '../../store/useAuthStore';
import { Avatar } from '../tasks/linear/Avatar';

/** Pick a colleague to DM — get-or-creates the conversation and jumps to it. */
export function NewDmModal({
  open,
  onClose,
  onOpened,
}: {
  open: boolean;
  onClose: () => void;
  onOpened: (conversationId: string) => void;
}) {
  const { t } = useI18n();
  const me = useAuthStore((s) => s.user);
  const { data: users } = useUsers();
  const openDm = useOpenDm();
  const [query, setQuery] = useState('');

  if (!open) return null;

  const candidates = (users ?? [])
    .filter((u) => u.id !== me?.id)
    .filter((u) => u.name.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div
      className="ui-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-label={t('teamChat.newDm')}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="ui-modal-panel w-full max-w-md">
        <h2 className="font-heading text-base font-semibold text-foreground">
          {t('teamChat.newDm')}
        </h2>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('teamChat.searchPeople')}
          className="ui-input mt-3"
          autoFocus
        />
        <div className="mt-3 max-h-72 space-y-0.5 overflow-y-auto">
          {candidates.map((user) => (
            <button
              key={user.id}
              type="button"
              disabled={openDm.isPending}
              onClick={() => {
                openDm.mutate(user.id, {
                  onSuccess: (conversation) => onOpened(conversation.id),
                });
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-accent disabled:opacity-50"
            >
              <Avatar name={user.name} size={28} />
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{user.name}</span>
                <span className="block truncate text-xs text-muted-foreground">{user.email}</span>
              </span>
            </button>
          ))}
          {candidates.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">{t('teamChat.noPeople')}</p>
          )}
        </div>
        <div className="mt-4 flex justify-end">
          <button type="button" onClick={onClose} className="ui-btn-secondary">
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
}
